import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Avatar, Dropdown, Typography, Space, Breadcrumb, theme, Drawer } from 'antd';
import AdminRoutes from './index';
import {
  DashboardOutlined,
  PrinterOutlined,
  ExperimentOutlined,
  LinkOutlined,
  LogoutOutlined,
  UserOutlined,
  HomeOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ProductOutlined,
  MenuOutlined,
} from '@ant-design/icons';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

interface AdminLayoutProps {
  onLogout: () => void;
  children?: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ onLogout, children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [userName, setUserName] = useState<string>('管理员');
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // 检测是否为移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 移动端菜单项点击后自动关闭
  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    if (isMobile) {
      setMobileMenuVisible(false);
    }
  };

  // 验证token有效性
  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      try {
        const response = await fetch('/api/admin/verify', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Token验证失败');
        }

        const data = await response.json();
        setUserName(data.username || '管理员');
      } catch (error) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_token_expiry');
        navigate('/admin/login');
      }
    };

    verifyToken();
  }, [navigate]);

  // 检查token是否过期
  useEffect(() => {
    const expiry = localStorage.getItem('admin_token_expiry');
    if (expiry && Date.now() > parseInt(expiry)) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_token_expiry');
      navigate('/admin/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_token_expiry');
    onLogout();
    navigate('/admin/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人资料',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  const menuItems = [
    {
      key: '/admin',
      icon: <DashboardOutlined />,
      label: '仪表板',
      path: '/admin',
    },
    {
      key: '/admin/printers',
      icon: <PrinterOutlined />,
      label: '打印机管理',
      path: '/admin/printers',
    },
    {
      key: '/admin/materials',
      icon: <ExperimentOutlined />,
      label: '材料管理',
      path: '/admin/materials',
    },
    {
      key: '/admin/associations',
      icon: <LinkOutlined />,
      label: '关联管理',
      path: '/admin/associations',
    },
    {
      key: '/admin/printed-products',
      icon: <ProductOutlined />,
      label: '成品管理',
      path: '/admin/printed-products',
    },
  ];

  const breadcrumbItems = [
    {
      title: <HomeOutlined />,
      href: '/',
    },
    {
      title: '管理后台',
      href: '/admin',
    },
    ...location.pathname
      .split('/')
      .filter(Boolean)
      .slice(1)
      .map((segment, index, arr) => {
        const path = '/' + arr.slice(0, index + 1).join('/');
        const labelMap: Record<string, string> = {
          'admin': '仪表板',
          'printers': '打印机管理',
          'materials': '材料管理',
          'associations': '关联管理',
          'printed-products': '成品管理',
        };
        
        return {
          title: labelMap[segment] || segment,
          href: index === arr.length - 1 ? undefined : path,
        };
      }),
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 桌面端侧边栏 */}
      {!isMobile && (
        <Layout.Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          style={{
            background: colorBgContainer,
            borderRight: '1px solid #f0f0f0',
          }}
        >
          <div style={{
            padding: collapsed ? '16px 8px' : '16px 24px',
            borderBottom: '1px solid #f0f0f0',
            textAlign: collapsed ? 'center' : 'left'
          }}>
            <Title
              level={collapsed ? 5 : 3}
              style={{
                margin: 0,
                color: '#d32f2f',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {collapsed ? '管理' : '3D打印管理'}
            </Title>
          </div>

          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            style={{ borderRight: 0, marginTop: '8px' }}
            items={menuItems.map(item => ({
              key: item.key,
              icon: item.icon,
              label: (
                <Link to={item.path} style={{ color: 'inherit' }}>
                  {item.label}
                </Link>
              ),
            }))}
            onClick={handleMenuClick}
          />
        </Layout.Sider>
      )}

      {/* 移动端侧边栏 Drawer */}
      {isMobile && (
        <Drawer
          title={<span style={{ color: '#d32f2f' }}>3D打印管理</span>}
          placement="left"
          closable={true}
          onClose={() => setMobileMenuVisible(false)}
          open={mobileMenuVisible}
          style={{ padding: 0 }}
          bodyStyle={{ padding: 0 }}
        >
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            style={{ borderRight: 0 }}
            items={menuItems.map(item => ({
              key: item.key,
              icon: item.icon,
              label: item.label,
            }))}
            onClick={handleMenuClick}
          />
        </Drawer>
      )}

      <Layout>
        <Header style={{
          padding: '0 16px',
          background: colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #f0f0f0',
        }}>
          {/* 移动端显示菜单按钮，桌面端显示折叠按钮 */}
          {isMobile ? (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileMenuVisible(true)}
              style={{ fontSize: '16px', width: 48, height: 64 }}
            />
          ) : (
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: '16px', width: 64, height: 64 }}
            />
          )}

          <Space size="large">
            <Button
              type="text"
              icon={<HomeOutlined />}
              onClick={() => navigate('/')}
            >
              返回主页
            </Button>

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer', padding: '8px' }}>
                <Avatar
                  icon={<UserOutlined />}
                  style={{ background: '#d32f2f' }}
                />
                {(!isMobile && !collapsed) && (
                  <>
                    <Text strong>{userName}</Text>
                    <Text type="secondary">管理员</Text>
                  </>
                )}
              </Space>
            </Dropdown>
          </Space>
        </Header>
        
        <Content style={{ margin: '24px 16px', padding: 0 }}>
          <div style={{ 
            marginBottom: '16px',
            padding: '16px 24px',
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
            borderBottom: '1px solid #f0f0f0',
          }}>
            <Breadcrumb items={breadcrumbItems} />
            <Title level={4} style={{ margin: '8px 0 0 0' }}>
              {breadcrumbItems[breadcrumbItems.length - 1]?.title || '管理后台'}
            </Title>
          </div>
          
          <div style={{ 
            padding: 24, 
            background: colorBgContainer, 
            borderRadius: borderRadiusLG,
            minHeight: 280,
          }}>
            <AdminRoutes />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
